import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CategoryIcon from '@mui/icons-material/Category';
import { getPublicCategoriesWithHierarchy, getEcommerceProducts } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';

interface Category {
  id: string;
  name: string;
  imageUrl?: string;
  level: number;
  parentId?: string | null;
}

interface EcommerceProductLite {
  id: string;
  name: string;
  stockCode?: string;
  categoryId?: string;
}

interface CategorySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (categories: Category[]) => void;
  companyId: string;
  selectedCategoryIds: string[];
  maxSelection?: number;
}

const CategorySelectionModal: React.FC<CategorySelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  companyId,
  selectedCategoryIds,
  maxSelection = 20,
}) => {
  const { t } = useTranslation('salesSystem');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [productsByCategory, setProductsByCategory] = useState<Record<string, EcommerceProductLite[]>>({});
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());

  // Modal açıldığında kategorileri yükle
  useEffect(() => {
    if (open && companyId) {
      loadCategories();
      setSelectedIds(new Set(selectedCategoryIds));
      setSearchTerm('');
      setPage(0);
      setExpandedCategoryIds(new Set());
    }
  }, [open, companyId, selectedCategoryIds]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      console.log('🔄 [CATEGORY MODAL] Hiyerarşik kategoriler yükleniyor...', { companyId });
      const hierarchy = await getPublicCategoriesWithHierarchy(companyId);

      const mainCategories: Category[] = (Array.isArray(hierarchy) ? hierarchy : [])
        .filter((cat: any) => {
          const level = Number(cat?.level ?? 0);
          const parentId = cat?.parentId;
          const isVisible = cat?.isVisible !== false;
          return isVisible && (level === 0 || parentId === null || parentId === undefined || parentId === '');
        })
        .map((cat: any) => ({
          id: String(cat?.id || ''),
          name: String(cat?.name || ''),
          imageUrl: cat?.imageUrl || undefined,
          level: 0,
          parentId: null
        }))
        .filter((c: Category) => c.id && c.name);

      console.log('✅ [CATEGORY MODAL] Ana kategoriler yüklendi:', mainCategories.length, 'kategori');
      setCategories(mainCategories);

      // Ana kategoriye ait ürünleri hazırla (açılır listede gösterim için)
      const ecommerceProducts = await getEcommerceProducts(companyId);
      const grouped = (Array.isArray(ecommerceProducts) ? ecommerceProducts : []).reduce(
        (acc: Record<string, EcommerceProductLite[]>, p: any) => {
          const categoryId = String(p?.categoryId || '').trim();
          if (!categoryId) return acc;
          if (!acc[categoryId]) acc[categoryId] = [];
          acc[categoryId].push({
            id: String(p?.id || ''),
            name: String(p?.name || ''),
            stockCode: String(p?.stockCode || ''),
            categoryId
          });
          return acc;
        },
        {}
      );
      setProductsByCategory(grouped);
    } catch (error) {
      console.error('❌ [CATEGORY MODAL] Kategoriler yüklenirken hata:', error);
      setCategories([]);
      setProductsByCategory({});
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    return categories.filter(category => 
      category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  const handleSelectCategory = (categoryId: string) => {
    const newSelection = new Set(selectedIds);
    
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      if (newSelection.size < maxSelection) {
        newSelection.add(categoryId);
      }
    }
    
    setSelectedIds(newSelection);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const visibleCategoryIds = filteredCategories
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(c => c.id);
      
      const newSelection = new Set(selectedIds);
      visibleCategoryIds.forEach(id => {
        if (newSelection.size < maxSelection) {
          newSelection.add(id);
        }
      });
      setSelectedIds(newSelection);
    } else {
      const visibleCategoryIds = filteredCategories
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(c => c.id);
      
      const newSelection = new Set(selectedIds);
      visibleCategoryIds.forEach(id => newSelection.delete(id));
      setSelectedIds(newSelection);
    }
  };

  const handleSave = () => {
    const selectedCategories = categories.filter(c => selectedIds.has(c.id));
    onSelect(selectedCategories);
  };

  const paginatedCategories = filteredCategories.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const isAllSelected = paginatedCategories.length > 0 && 
    paginatedCategories.every(c => selectedIds.has(c.id));

  const isIndeterminate = paginatedCategories.some(c => selectedIds.has(c.id)) && 
    !isAllSelected;

  const getCategoryDisplayName = (category: Category) => {
    const indent = '  '.repeat(category.level);
    return `${indent}${category.name}`;
  };

  const toggleExpandCategory = (categoryId: string) => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="category-selection-modal-title"
    >
      <DialogTitle id="category-selection-modal-title">
        {t('ecommerceCategorySelectionModal.title')}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('ecommerceCategorySelectionModal.selectedCount', { selected: selectedIds.size, max: maxSelection })}
          </Typography>
          
          <TextField
            fullWidth
            size="small"
            placeholder={t('ecommerceCategorySelectionModal.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {selectedIds.size >= maxSelection && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('ecommerceCategorySelectionModal.maxSelectionWarning', { max: maxSelection })}
            </Alert>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        disabled={selectedIds.size >= maxSelection && !isAllSelected}
                      />
                    </TableCell>
                    <TableCell>{t('ecommerceCategorySelectionModal.table.image')}</TableCell>
                    <TableCell>{t('ecommerceCategorySelectionModal.table.categoryName')}</TableCell>
                    <TableCell>{t('ecommerceCategorySelectionModal.table.level')}</TableCell>
                    <TableCell>{t('ecommerceCategorySelectionModal.table.products')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCategories.map((category) => {
                    const isSelected = selectedIds.has(category.id);
                    const canSelect = selectedIds.size < maxSelection || isSelected;
                    const categoryProducts = productsByCategory[category.id] || [];
                    const isExpanded = expandedCategoryIds.has(category.id);
                    
                    return (
                      <React.Fragment key={category.id}>
                        <TableRow
                          hover
                          role="checkbox"
                          selected={isSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectCategory(category.id)}
                              disabled={!canSelect}
                            />
                          </TableCell>
                          <TableCell>
                            {category.imageUrl ? (
                              <Avatar
                                src={category.imageUrl}
                                alt={category.name}
                                sx={{ width: 40, height: 40 }}
                              />
                            ) : (
                              <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}>
                                <CategoryIcon />
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  pl: category.level * 2,
                                  fontWeight: category.level === 0 ? 'bold' : 'normal'
                                }}
                              >
                                {getCategoryDisplayName(category)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={category.level === 0 ? t('ecommerceCategorySelectionModal.level.main') : t('ecommerceCategorySelectionModal.level.sub', { level: category.level })}
                              size="small"
                              color={category.level === 0 ? 'primary' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={t('ecommerceCategorySelectionModal.productCount', { count: categoryProducts.length })}
                                size="small"
                                color={categoryProducts.length > 0 ? 'success' : 'default'}
                                variant="outlined"
                              />
                              <IconButton
                                size="small"
                                onClick={() => toggleExpandCategory(category.id)}
                                disabled={categoryProducts.length === 0}
                              >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1, px: 2, bgcolor: '#fafafa' }}>
                                <List dense disablePadding>
                                  {categoryProducts.slice(0, 20).map((p) => (
                                    <ListItem key={p.id} disableGutters>
                                      <ListItemText
                                        primary={p.name}
                                        secondary={p.stockCode ? `SKU: ${p.stockCode}` : undefined}
                                      />
                                    </ListItem>
                                  ))}
                                  {categoryProducts.length > 20 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {t('ecommerceCategorySelectionModal.moreProducts', { count: categoryProducts.length - 20 })}
                                    </Typography>
                                  )}
                                </List>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCategories.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('ecommerceCategorySelectionModal.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          {t('ecommerceCategorySelectionModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={selectedIds.size === 0}
        >
          {t('ecommerceCategorySelectionModal.actions.select', { count: selectedIds.size })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategorySelectionModal;
